/**
 * Gatilhos de frase: "quando eu falar X, use a opção Y da clínica".
 *
 * Pedido do Leopoldo (19/09/2026). Dois casos reais que motivaram isto:
 *
 *  1. Ele dita "trazer até março de 2027" e o papel tem que sair com
 *     "Retorno para mostrar exames até março de 2027" — a forma canônica
 *     que já está na lista de motivos da Próxima Avaliação, não o verbo
 *     solto que ele falou.
 *  2. Ele diz "uso contínuo" numa receita e a nota "NÃO SUSPENDER" tem que
 *     entrar sozinha. O casamento por TÍTULO (`matchNoteTemplates`) não
 *     pega isso: "uso contínuo" não contém "não" nem "suspender".
 *
 * A camada é DETERMINÍSTICA de propósito: o LLM não escolhe a frase do
 * papel. Ele só transcreve; o mapeamento fala → texto canônico é tabela.
 *
 * Persistência: localStorage por navegador, igual aos padrões (estrela) de
 * `notesDefaults.ts` e `lensObservations.ts`. As sementes abaixo fazem o
 * recurso funcionar no primeiro ditado, sem configurar nada; o que o médico
 * editar passa a valer NO LUGAR da semente daquele item (a semente não
 * volta a aparecer — senão não daria pra remover uma frase ruim).
 */

/** Cada família de opção que aceita gatilho. */
export type TriggerKind =
  | 'prescriptionNotes'
  | 'examNotes'
  | 'surgeryText'
  | 'returnReason';

/** chave normalizada do item (título/motivo) → frases que o disparam. */
export type TriggerMap = Record<string, string[]>;

const KEY_PREFIX = 'dratento-phrase-triggers:';

function ls(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/** Sem acento, minúsculo, só letras/números/espaço. */
export function normPhrase(s: string): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Chave do item: o título/motivo normalizado (id de modelo muda, título não). */
export function triggerKey(titleOrReason: string): string {
  return normPhrase(titleOrReason);
}

/**
 * Sementes. Chaveadas pelo título/motivo como o Leopoldo os tem cadastrados;
 * item que ele não tenha com esse nome simplesmente não casa com nada, o que
 * é inofensivo.
 */
export const DEFAULT_TRIGGERS: Record<TriggerKind, TriggerMap> = {
  prescriptionNotes: {
    [triggerKey('NÃO SUSPENDER')]: [
      'uso continuo', 'uso continuado', 'nao suspender', 'nao pode parar',
      'nao interromper', 'nao para o colirio', 'usar sempre', 'para sempre',
    ],
    [triggerKey('NÃO SUSPENDER ATÉ RETORNO')]: [
      'nao suspender ate o retorno', 'usar ate o retorno', 'ate voltar aqui',
    ],
    [triggerKey('GELADEIRA')]: [
      'geladeira', 'guardar na geladeira', 'manter refrigerado', 'refrigerar',
    ],
    [triggerKey('INTERVALO')]: [
      'intervalo entre os colirios', 'intervalo de cinco minutos', 'cinco minutos entre',
      'esperar cinco minutos', 'um colirio de cada vez',
    ],
    [triggerKey('OLHO OPERADO')]: ['olho operado', 'so no olho operado'],
    [triggerKey('COMPRESSA GELADA')]: ['compressa gelada', 'compressa fria', 'gelo no olho'],
    [triggerKey('COMPRESSA QUENTE')]: ['compressa quente', 'compressa morna', 'calor no olho'],
    [triggerKey('LENTES - ORIENTAÇÕES (COLIRIOS)')]: [
      'usa lente de contato', 'com lente de contato', 'tirar a lente',
    ],
  },
  examNotes: {
    [triggerKey('AGENDAR EXAMES')]: [
      'agendar os exames', 'agendar exame', 'marcar os exames', 'marcar exame',
      'agendar na recepcao',
    ],
    [triggerKey('ENVIAR EXAMES')]: ['enviar os exames', 'mandar os exames', 'enviar por whatsapp'],
    [triggerKey('ENVIO DE RESULTADO DE EXAMES')]: [
      'enviar o resultado', 'mandar o resultado', 'resultado por whatsapp',
    ],
  },
  surgeryText: {
    [triggerKey('CONTRA INDICAÇÃO')]: ['contra indicacao', 'contraindicacao', 'contraindicado'],
  },
  returnReason: {
    [triggerKey('Retorno para mostrar exames')]: [
      'trazer', 'trazer os exames', 'trazer exames', 'mostrar exames',
      'mostrar os exames', 'voltar com os exames', 'retorno com os exames',
      'retorno para mostrar', 'retorno com exames',
    ],
    [triggerKey('Retorno (acompanhamento)')]: [
      'retorno de acompanhamento', 'acompanhamento', 'controle', 'seguimento',
    ],
    [triggerKey('Nova consulta')]: ['nova consulta', 'consulta nova'],
    [triggerKey('Retorno pós-cirúrgico')]: [
      'pos operatorio', 'pos cirurgico', 'retorno da cirurgia', 'revisao da cirurgia',
    ],
    [triggerKey('Retorno para revisão')]: ['revisao', 'revisar'],
  },
};

/**
 * Gatilhos efetivos de uma família: o que o médico editou VENCE a semente,
 * item a item. Item nunca editado usa a semente.
 */
export function loadTriggers(kind: TriggerKind): TriggerMap {
  const seeds = DEFAULT_TRIGGERS[kind] ?? {};
  try {
    const raw = ls()?.getItem(KEY_PREFIX + kind);
    if (!raw) return { ...seeds };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { ...seeds };
    const saved: TriggerMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(v) && v.every((x) => typeof x === 'string')) saved[k] = v;
    }
    return { ...seeds, ...saved };
  } catch {
    return { ...seeds };
  }
}

/** Frases de UM item (já com a semente aplicada quando nunca foi editado). */
export function triggersFor(kind: TriggerKind, titleOrReason: string): string[] {
  return loadTriggers(kind)[triggerKey(titleOrReason)] ?? [];
}

/**
 * Grava as frases de um item. Lista vazia grava vazio DE PROPÓSITO: é assim
 * que o médico desliga uma semente que não serve pra ele.
 */
export function saveTriggersFor(kind: TriggerKind, titleOrReason: string, phrases: string[]): void {
  const store = ls();
  if (!store) return;
  let atual: Record<string, string[]> = {};
  try {
    const raw = store.getItem(KEY_PREFIX + kind);
    if (raw) atual = JSON.parse(raw) as Record<string, string[]>;
  } catch { /* ignore */ }
  const limpas = phrases.map((p) => p.trim()).filter(Boolean);
  atual[triggerKey(titleOrReason)] = limpas;
  store.setItem(KEY_PREFIX + kind, JSON.stringify(atual));
}

/**
 * A fala dispara este item?
 *
 * Frase de UMA palavra exige a palavra inteira ("geladeira" não casa com
 * "geladeirinha"); frase com duas ou mais casa por trecho contínuo, que é
 * como a pessoa fala ("agendar os exames" dentro de "vou agendar os exames
 * na recepção").
 */
export function phrasesMatch(fala: string, phrases: string[]): boolean {
  const texto = normPhrase(fala);
  if (!texto) return false;
  const tokens = texto.split(' ');
  return phrases.some((p) => {
    const alvo = normPhrase(p);
    if (!alvo) return false;
    return alvo.includes(' ') ? texto.includes(alvo) : tokens.includes(alvo);
  });
}

/** Itens (por título/motivo) cujos gatilhos aparecem na fala. */
export function matchByTriggers<T>(
  fala: string,
  items: T[],
  keyOf: (item: T) => string,
  kind: TriggerKind,
  triggers: TriggerMap = loadTriggers(kind),
): T[] {
  return items.filter((item) => phrasesMatch(fala, triggers[triggerKey(keyOf(item))] ?? []));
}

/**
 * A opção carrega um prazo no próprio texto ("EM 06 MESES", "em 1 ano")?
 *
 * Nota assim NÃO entra quando o médico ditou um mês: o papel sairia com dois
 * prazos se contradizendo ("…até março de 2027" + "…em 06 meses"). Decisão
 * do Leopoldo, 19/09/2026.
 */
export function hasFixedPeriod(...textos: string[]): boolean {
  const alvo = normPhrase(textos.join(' '));
  return /\b(em|apos|daqui a)\s+\d{1,2}\s*(dia|dias|semana|semanas|mes|meses|ano|anos)\b/.test(alvo)
    || /\b\d{1,2}\s*(meses|anos)\b/.test(alvo);
}
